import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Contract, ContractReplacement, Milestone } from '../../types';
import { contractTitle } from '../../utils/contractTitle';
import Modal from '../Modal';
import ContractTimeline from '../ContractTimeline';
import { combinedAgreementTimeline } from '../../utils/combinedAgreementTimeline';
import { variationMoney } from './VariationPlanStep';
import { agreementBasis, agreementLabel, agreementRoot } from '../../utils/agreementIdentity';
import { variationBlock, variationBlockText } from '../../utils/variationBlock';

export function agreementChain(current: Contract, contracts: Contract[]) {
  const path: Contract[] = [current];
  const seen = new Set([current.id]);
  let head = current;
  while (
    head.stageVariation?.originalContractId ||
    head.replacesContractId ||
    head.parentContractId
  ) {
    const id =
      head.stageVariation?.originalContractId ??
      head.replacesContractId ??
      head.parentContractId;
    const parent = contracts.find(
      (c) => c.id === id && c.projectId === current.projectId,
    );
    if (!parent || seen.has(parent.id)) break;
    path.unshift(parent);
    seen.add(parent.id);
    head = parent;
  }
  const prices = contracts.filter(
    (item) =>
      item.projectId === current.projectId &&
      item.settlementType === 'price_adjustment' &&
      item.parentContractId &&
      seen.has(item.parentContractId) &&
      !seen.has(item.id) &&
      item.companySigned &&
      item.personSigned,
  );
  return [...path, ...prices].sort(
    (left, right) =>
      Date.parse(left.createdAt) - Date.parse(right.createdAt) ||
      left.contractNumber - right.contractNumber,
  );
}
export function VariationAgreementModal({
  contract,
  contracts,
  stages,
  onClose,
  onCreateVariation,
  onSignedDocument,
  onEdit,
  viewerRole,
  replacements,
}: {
  contract: Contract;
  contracts: Contract[];
  stages: Milestone[];
  /** Settlement status per contract, to tell why an ÄTA is not finished. */
  replacements?: Record<string, ContractReplacement>;
  onClose: () => void;
  onCreateVariation: (id: string) => void;
  onSignedDocument: (contract: Contract) => void;
  onEdit: (contract: Contract) => void;
  viewerRole: 'company' | 'person';
}) {
  const { t } = useTranslation();
  const [selectedId, setSelectedId] = useState(contract.id);
  const [combined, setCombined] = useState(true);
  const selected = contracts.find((c) => c.id === selectedId) ?? contract;
  const chain = agreementChain(contract, contracts);
  const basis =
    [...chain]
      .reverse()
      .find((item) => item.settlementType !== 'price_adjustment') ?? contract;
  const projection = combinedAgreementTimeline(basis, contracts, stages);
  const current = [...chain]
    .reverse()
    .find(
      (c) =>
        c.draftStatus === 'signed_by_both' &&
        (c.versionStatus ?? 'current') === 'current' &&
        c.settlementType !== 'price_adjustment',
    );
  const signed = selected.companySigned && selected.personSigned;
  const pending = current && contracts.find(item => item.projectId === current.projectId &&
    item.versionStatus === 'proposed' && item.stageVariation?.originalContractId === current.id);
  // A sent, signed or unpaid ÄTA cannot be continued or followed by a new one (ClickUp 869fkk2jh).
  const block = current ? variationBlock(current, contracts, replacements) : null;
  const locked =
    signed ||
    selected.companySigned ||
    selected.personSigned ||
    ['sent', 'pending_signature'].includes(selected.signingStatus ?? '');
  const label = (c: Contract) => agreementLabel(c, contracts, t);
  const selectedBasis = agreementBasis(selected, contracts, t);
  const settled =
    selected.settlementType !== 'price_adjustment' ||
    stages.some(
      (stage) =>
        stage.contractId === selected.id &&
        (stage.deliveryStatus === 'paid' ||
          (Number(selected.afterRotAmount) === 0 &&
            stage.companyAttestation === 'approved' &&
            stage.personAttestation === 'approved')),
    );
  const active = (selected.versionStatus ?? 'current') === 'current' && settled;
  return (
    <Modal
      title={t('variationWizard.agreementTitle')}
      onClose={onClose}
      size="xl"
      fullscreenBelow="md"
      dialogClassName="variation-agreement-modal"
    >
      <div className="row g-4">
        <aside className="col-12 col-lg-3">
          <details>
            <summary className="fs-6 fw-semibold py-3 variation-wizard-touch">{t('variationWizard.chain')}</summary>
            <div className="d-flex flex-column gap-2 mt-2">
              {chain.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className={`btn d-block text-start variation-wizard-touch ${selected.id === c.id ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => setSelectedId(c.id)}
                >
                  <strong className="d-block">{label(c)}</strong>
                  <small className="d-block">{contractTitle(c)}</small>
                </button>
              ))}
            </div>
          </details>
          <div className="d-flex flex-wrap gap-2 mt-3">
            {signed && (
              <button
                type="button"
                className="btn btn-outline-success variation-wizard-touch"
                onClick={() => onSignedDocument(selected)}
              >
                {t('contracts.list.actionSignedDocument')}
              </button>
            )}
            {viewerRole === 'company' && !locked && (
              <button
                type="button"
                className="btn btn-outline-primary variation-wizard-touch"
                onClick={() => onEdit(selected)}
              >
                {t('contracts.list.actionEdit')}
              </button>
            )}
            {/* An ÄTA still being drafted or signed is continued in the guide,
                which shows its signing status (ClickUp 869fkk2jh). */}
            {viewerRole === 'company' && current && (!block || block.reason === 'signing') && (
              <div className="w-100" data-testid={block ? 'variation-blocked' : undefined}>
                <button
                  type="button"
                  className="btn btn-primary variation-wizard-touch"
                  onClick={() => pending ? onEdit(pending) : onCreateVariation(current.id)}
                >
                  {pending ? t('variationWizard.continueVariation', { agreement: label(pending) }) : t('variationWizard.create')}
                </button>
                {block && <p className="small text-muted mt-2 mb-0">{variationBlockText(block, contracts, t)}</p>}
              </div>
            )}
            {/* A signed ÄTA waiting for the deposit, refund or activation blocks a new one. */}
            {viewerRole === 'company' && current && block && block.reason !== 'signing' && (
              <div className="w-100" data-testid="variation-blocked">
                <button type="button" className="btn btn-primary variation-wizard-touch" disabled>
                  {t('variationWizard.create')}
                </button>
                <p className="small text-muted mt-2 mb-0">
                  {variationBlockText(block, contracts, t)}{' '}
                  {block.contract.id !== selected.id && (
                    <button type="button" className="btn btn-link btn-sm p-0 align-baseline variation-wizard-touch" onClick={() => setSelectedId(block.contract.id)}>
                      {t('variationWizard.blocked.open')}
                    </button>
                  )}
                </p>
              </div>
            )}
          </div>
        </aside>
        <section className="col-12 col-lg-9">
          <h2 className="fs-4">
            {label(selected)} · {contractTitle(selected)}
          </h2>
          {selectedBasis && <p className="small text-muted mb-2">{selectedBasis}</p>}
          <p className="text-muted">
            {selected.stageVariation
              ? variationMoney(selected.stageVariation.afterRotDeltaOre)
              : variationMoney(
                  Math.round(Number(selected.afterRotAmount) * 100),
                )}{' '}
            {t('variationWizard.afterRot')}
          </p>
          <ol className="variation-agreement-status list-unstyled d-flex flex-wrap gap-3 border rounded p-3">
            <li
              className={
                selected.companySigned ? 'text-success' : 'fw-semibold'
              }
            >
              {t('variationWizard.companySign')}{' '}
              {selected.companySigned ? '✓' : ''}
            </li>
            <li className={selected.personSigned ? 'text-success' : ''}>
              {t('variationWizard.customerSign')}{' '}
              {selected.personSigned ? '✓' : ''}
            </li>
            <li>{t('variationWizard.settlement')}</li>
            <li className={signed && active ? 'text-success' : ''}>
              {t('variationWizard.applies')} {signed && active ? '✓' : ''}
            </li>
          </ol>
          <div
            className="d-flex gap-2 flex-wrap mb-3"
            role="group"
            aria-label={t('variationWizard.timelineMode')}
          >
            <button
              type="button"
              className={`btn variation-wizard-touch ${combined ? 'btn-primary' : 'btn-outline-secondary'}`}
              aria-pressed={combined}
              onClick={() => setCombined(true)}
            >
              {t('variationWizard.combinedTimeline')}
            </button>
            <button
              type="button"
              className={`btn variation-wizard-touch ${!combined ? 'btn-primary' : 'btn-outline-secondary'}`}
              aria-pressed={!combined}
              onClick={() => setCombined(false)}
            >
              {t('variationWizard.agreementTimeline')}
            </button>
          </div>
          <ContractTimeline
            contract={combined ? (projection?.contract ?? basis) : selected}
            milestones={
              combined && projection
                ? projection.milestones
                : stages.filter((stage) => stage.contractId === selected.id && (
                    !selected.stageVariation || !stage.replacesMilestoneId ||
                    selected.stageVariation.selectedMilestoneIds.includes(stage.replacesMilestoneId) ||
                    selected.stageVariation.restoredMilestones?.some(link => link.milestoneId === stage.id)
                  ))
            }
            combinedRoot={combined ? projection?.root : undefined}
            milestoneContexts={combined ? projection?.contexts : undefined}
            relatedContracts={contracts}
            relatedMilestones={stages}
            parentContract={agreementRoot(selected, contracts)}
            viewerRole={viewerRole}
            agreementReadOnly={!combined}
            agreementProjection={!combined}
          />
        </section>
      </div>
    </Modal>
  );
}
